﻿import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RevealWrapper from './RevealWrapper';
import { useTypewriter } from '@/hooks/useTypewriter';
import { formatDownloadCount } from '@/config/productStats';
import { useProductStats } from '@/context/ProductStatsContext';

type TokenType = 'keyword' | 'type' | 'string' | 'comment' | 'function' | 'number' | 'annotation' | 'plain' | 'operator' | 'punctuation';

interface Token {
  type: TokenType;
  value: string;
}

interface CodeLine {
  tokens: Token[];
  indent: number;
  delay: number;
}

const TOKEN_COLORS: Record<TokenType, string> = {
  keyword:    'text-[#c792ea]',
  type:       'text-[#ffcb6b]',
  string:     'text-[#c3e88d]',
  comment:    'text-[#546e7a]',
  function:   'text-[#82aaff]',
  number:     'text-[#f78c6c]',
  annotation: 'text-[#c792ea]',
  plain:      'text-[#d4d4d4]',
  operator:   'text-[#89ddff]',
  punctuation:'text-[#89ddff]',
};

const CODE_LINES: CodeLine[] = [
  { indent: 0, delay: 0,    tokens: [{ type: 'comment', value: '// G-Software Â· DefterKasa a1.0.1' }] },
  { indent: 0, delay: 300,  tokens: [{ type: 'plain', value: '' }] },
  { indent: 0, delay: 500,  tokens: [
    { type: 'keyword', value: 'using ' },
    { type: 'type', value: 'Microsoft.Maui.Controls' },
    { type: 'punctuation', value: ';' },
  ]},
  { indent: 0, delay: 750,  tokens: [
    { type: 'keyword', value: 'using ' },
    { type: 'type', value: 'DefterKasa.ViewModels' },
    { type: 'punctuation', value: ';' },
  ]},
  { indent: 0, delay: 1000, tokens: [{ type: 'plain', value: '' }] },
  { indent: 0, delay: 1150, tokens: [
    { type: 'keyword', value: 'namespace ' },
    { type: 'type', value: 'DefterKasa.Views' },
    { type: 'punctuation', value: ';' },
  ]},
  { indent: 0, delay: 1350, tokens: [{ type: 'plain', value: '' }] },
  { indent: 0, delay: 1500, tokens: [
    { type: 'keyword', value: 'public partial class ' },
    { type: 'function', value: 'HomePage' },
    { type: 'operator', value: ' : ' },
    { type: 'type', value: 'ContentPage' },
  ]},
  { indent: 0, delay: 1750, tokens: [{ type: 'punctuation', value: '{' }] },
  { indent: 1, delay: 1950, tokens: [
    { type: 'keyword', value: 'private readonly ' },
    { type: 'type', value: 'HomeViewModel ' },
    { type: 'plain', value: '_viewModel' },
    { type: 'punctuation', value: ';' },
  ]},
  { indent: 1, delay: 2200, tokens: [{ type: 'plain', value: '' }] },
  { indent: 1, delay: 2350, tokens: [
    { type: 'keyword', value: 'public ' },
    { type: 'function', value: 'HomePage' },
    { type: 'punctuation', value: '()' },
  ]},
  { indent: 1, delay: 2550, tokens: [{ type: 'punctuation', value: '{' }] },
  { indent: 2, delay: 2750, tokens: [
    { type: 'function', value: 'InitializeComponent' },
    { type: 'punctuation', value: '();' },
  ]},
  { indent: 2, delay: 2950, tokens: [
    { type: 'plain', value: '_viewModel ' },
    { type: 'operator', value: '= ' },
    { type: 'keyword', value: 'new ' },
    { type: 'type', value: 'HomeViewModel' },
    { type: 'punctuation', value: '();' },
  ]},
  { indent: 2, delay: 3150, tokens: [
    { type: 'plain', value: 'BindingContext ' },
    { type: 'operator', value: '= ' },
    { type: 'plain', value: '_viewModel' },
    { type: 'punctuation', value: ';' },
  ]},
  { indent: 1, delay: 3350, tokens: [{ type: 'punctuation', value: '}' }] },
  { indent: 0, delay: 3500, tokens: [{ type: 'punctuation', value: '}' }] },
  { indent: 0, delay: 3700, tokens: [{ type: 'plain', value: '' }] },
  { indent: 0, delay: 3900, tokens: [
    { type: 'comment', value: '// “ Build successful · 0 errors · 0 warnings' },
  ]},
];

function CodeTerminal() {
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    CODE_LINES.forEach((line, i) => {
      const t = setTimeout(() => setVisibleCount(i + 1), line.delay);
      timers.push(t);
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="border border-white/10 bg-[#0d1117] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/8 bg-[#161b22]">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
          <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
          <div className="w-3 h-3 rounded-full bg-[#28c840]" />
        </div>
        <span className="font-mono text-xs text-neutral-600">HomeScreen.kt - DefterKasa</span>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] text-neutral-700 border border-white/5 px-2 py-0.5">C#</span>
        </div>
      </div>

      <div className="p-5 overflow-hidden">
        <div className="space-y-0">
          {CODE_LINES.map((line, i) => {
            const lineNum = i + 1;
            const isVisible = i < visibleCount;
            const isLast = i === visibleCount - 1;
            if (!isVisible) return null;
            const indentStr = '\u00a0'.repeat(line.indent * 4);
            return (
              <div key={i} className="flex items-start font-mono text-[12px] lg:text-[13px] leading-6 min-h-[24px] group">
                <span className="select-none text-neutral-700 w-7 shrink-0 text-right mr-4 text-xs leading-6 group-hover:text-neutral-500 transition-colors">
                  {lineNum}
                </span>
                <span className="text-neutral-800 select-none whitespace-pre">{indentStr}</span>
                <span className="flex flex-wrap">
                  {line.tokens.map((tok, j) => (
                    <span key={j} className={TOKEN_COLORS[tok.type]}>{tok.value}</span>
                  ))}
                </span>
                {isLast && visibleCount < CODE_LINES.length && (
                  <span className="inline-block w-[2px] h-[14px] bg-white/70 ml-0.5 mt-1 animate-pulse" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between px-4 py-2 border-t border-white/5 bg-[#161b22]">
        <div className="flex items-center gap-4">
          <span className="font-mono text-[10px] text-neutral-700">
            <span className="text-[#28c840]">â—</span> .NET 9 MAUI
          </span>
          <span className="font-mono text-[10px] text-neutral-700">UTF-8</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="font-mono text-[10px] text-neutral-700">Ln {visibleCount}, Col 1</span>
          <span className="font-mono text-[10px] text-neutral-700">Visual Studio 2022</span>
        </div>
      </div>
    </div>
  );
}

export default function HeroSection() {
  const { t } = useTranslation();
  const defterKasaStats = useProductStats('defter-kasa');
  const [count, setCount] = useState(0);

  const TYPED_WORDS = [
    t('hero.typed.0'),
    t('hero.typed.1'),
    t('hero.typed.2'),
    t('hero.typed.3'),
    t('hero.typed.4'),
  ];

  const { text: typedText, wordIndex } = useTypewriter({
    words: TYPED_WORDS,
    typeSpeed: 75,
    deleteSpeed: 40,
    pauseAfterType: 2200,
    pauseAfterDelete: 350,
  });

  useEffect(() => {
    const end = defterKasaStats.downloads;
    if (end <= 0) {
      setCount(0);
      return;
    }

    let start = 0;
    const step = Math.max(1, Math.ceil(end / (2000 / 16)));
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [defterKasaStats.downloads]);

  return (
    <section className="relative min-h-screen bg-[#0c0c0c] flex flex-col overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }}
      />
      <div className="absolute top-20 right-0 w-px h-64 bg-gradient-to-b from-transparent via-white/15 to-transparent" />
      <div className="absolute top-20 right-0 w-64 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <div className="absolute bottom-20 left-0 w-px h-64 bg-gradient-to-b from-transparent via-white/15 to-transparent" />
      <div className="absolute bottom-20 left-0 w-64 h-px bg-gradient-to-r from-white/15 via-white/15 to-transparent" />

      <div className="flex-1 flex flex-col justify-center px-6 lg:px-14 pt-28 pb-16">
        <div className="grid lg:grid-cols-2 gap-16 xl:gap-28 items-center">
          <RevealWrapper direction="left" duration={700}>
            <div>
              <p className="font-mono text-xs text-neutral-600 tracking-widest uppercase mb-10">
                {t('hero.label')}
              </p>
              <h1 className="font-black text-white leading-none tracking-tighter mb-10">
                <span className="block text-[clamp(3rem,7vw,7rem)] text-neutral-600 relative">
                  <span>{typedText}</span>
                  <span
                    className="inline-block w-[4px] ml-1 bg-neutral-500 align-middle animate-pulse"
                    style={{ height: 'clamp(2.4rem,5.6vw,5.6rem)' }}
                  />
                </span>
              </h1>

              <div className="flex items-center gap-2 mb-8">
                {TYPED_WORDS.map((w, i) => (
                  <button
                    key={w}
                    aria-label={w}
                    className={`rounded-full transition-all duration-300 cursor-default ${
                      i === wordIndex ? 'w-5 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-neutral-700'
                    }`}
                  />
                ))}
              </div>

              <p className="text-neutral-500 text-base leading-loose max-w-sm mb-12">
                {t('hero.description')}
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <a
                  href="#products"
                  onClick={(e) => { e.preventDefault(); document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' }); }}
                  className="group relative inline-flex items-center gap-2 px-7 py-3.5 bg-white text-black font-mono text-xs font-bold tracking-widest uppercase overflow-hidden transition-all duration-300 hover:scale-105 sm:whitespace-nowrap cursor-pointer"
                >
                  <span className="relative z-10 group-hover:text-white transition-colors duration-300">{t('hero.cta.products')}</span>
                  <i className="ri-arrow-right-line relative z-10 group-hover:text-white transition-colors duration-300" />
                  <div className="absolute inset-0 bg-black translate-x-[-101%] group-hover:translate-x-0 transition-transform duration-300" />
                </a>
              </div>
            </div>
          </RevealWrapper>

          <RevealWrapper direction="right" delay={200} duration={700}>
            <div className="relative">
              <CodeTerminal />
              <div className="grid grid-cols-3 gap-px mt-px bg-white/5">
                {[ /* canlÄ± uygulama bilgisi deÄŸiÅŸtirme */
                  { value: formatDownloadCount(count), labelKey: 'hero.stats.downloads' },
                  { value: '1', labelKey: 'hero.stats.apps' },
                  { value: defterKasaStats.rating, labelKey: 'hero.stats.rating' },
                ].map((s) => (
                  <div key={s.labelKey} className="bg-[#111111] px-5 py-4 text-center">
                    <div className="font-black text-white text-xl tabular-nums">{s.value}</div>
                    <div className="font-mono text-xs text-neutral-600 uppercase tracking-wider mt-1">{t(s.labelKey)}</div>
                  </div>
                ))}
              </div>
            </div>
          </RevealWrapper>
        </div>
      </div>

      <div className="border-t border-white/5 px-6 lg:px-14 py-6 flex items-center justify-between">
        <span className="font-mono text-xs text-neutral-700">{t('hero.footer.version')}</span>
        <span className="font-mono text-xs text-neutral-700">{t('hero.footer.tags')}</span>
      </div>
    </section>
  );
}

